import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import {
  blueprintDeviceKey,
  blueprintDeviceLabel,
} from '@/services/dawPluginDetection/blueprintDetectionTypes';
import type { useBlueprintDetection } from './useBlueprintDetection';

export function BlueprintDetectionPanel({
  detection,
  disabled,
  isPreset,
  onUsePlugin,
  onUseVersion,
}: {
  detection: ReturnType<typeof useBlueprintDetection>;
  disabled: boolean;
  isPreset: boolean;
  onUsePlugin: (name: string) => void;
  onUseVersion: (version: string) => void;
}) {
  if (detection.status === 'idle') return null;
  const { result, selected } = detection;
  const devices = [...(result?.devices || []), ...(result?.suggestions || [])];
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <GraphicEqIcon fontSize="small" color="primary" />
        <Typography
          variant="body2"
          fontWeight={600}
          role="status"
          aria-live="polite"
        >
          {detection.status === 'checking'
            ? 'Checking devices…'
            : devices.length
              ? 'Devices found in your file'
              : 'Add your device requirements'}
        </Typography>
      </Stack>
      {detection.status === 'checking' ? (
        <LinearProgress
          aria-label="Checking Blueprint devices"
          sx={{ mt: 1.5 }}
        />
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, lineHeight: 1.6 }}
          >
            {detection.status === 'failed'
              ? detection.message
              : devices.length
                ? 'Selected devices will be included in this version’s requirements. Click a device to include or remove it.'
                : 'We couldn’t identify the devices automatically. You can still upload this file and enter them below.'}
          </Typography>
          {!!devices.length && (
            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              gap={1}
              sx={{ mt: 1.5 }}
            >
              {devices.map((device) => {
                const active = selected.some(
                  (d) => blueprintDeviceKey(d) === blueprintDeviceKey(device)
                );
                const suggestion = device.detectionMethod !== 'structured';
                return (
                  <Chip
                    key={blueprintDeviceKey(device)}
                    label={`${blueprintDeviceLabel(device)}${suggestion ? ' · suggested' : ''}`}
                    icon={active ? <CheckIcon /> : <AddIcon />}
                    variant={active ? 'filled' : 'outlined'}
                    color={active ? 'primary' : 'default'}
                    aria-pressed={active}
                    disabled={disabled}
                    onClick={() => {
                      detection.toggle(device);
                      if (isPreset && !active) onUsePlugin(device.name);
                    }}
                    sx={{
                      maxWidth: '100%',
                      height: 'auto',
                      minHeight: 32,
                      '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                    }}
                  />
                );
              })}
            </Stack>
          )}
          {result?.incomplete && !!devices.length && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              Some devices could not be identified. Add any missing plug-ins,
              Max for Live devices, or libraries below.
            </Typography>
          )}
          {(result?.savedWithVersion || (isPreset && result?.pluginName)) && (
            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              gap={1}
              sx={{ mt: 1 }}
            >
              {isPreset && result?.pluginName && (
                <Button
                  size="small"
                  disabled={disabled}
                  onClick={() => onUsePlugin(result.pluginName!)}
                >
                  Use {result.pluginName}
                </Button>
              )}
              {result?.savedWithVersion &&
                isPreset !== !!result.savedWithHost && (
                  <Button
                    size="small"
                    disabled={disabled}
                    onClick={() => onUseVersion(result.savedWithVersion!)}
                  >
                    Use saved-with version {result.savedWithVersion}
                  </Button>
                )}
            </Stack>
          )}
          {result?.savedWithVersion && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Saved with{' '}
              {result.savedWithHost
                ? 'Ableton Live'
                : result.pluginName || 'plug-in version'}{' '}
              {result.savedWithVersion}. Confirm the minimum version you’ve
              tested.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
